<template>
  <div class="staff-component" :class="{ inactive: isInactive }">
    <header @click="openUserDetails">
      <div class="avatar">
        <font-awesome-icon icon="user" />
      </div>
      <div class="staff-info">
        <div class="name-row">
          <h3>{{ staffMember.value.firstName }} {{ staffMember.value.lastName }}</h3>
          <span v-if="staffMember.value.manager" class="manager-badge"><font-awesome-icon icon="fa-star" /> Manager</span>
        </div>
        <span v-if="staffMember.value.active" class="payroll">{{ formsProgress }}</span>
        <span v-else class="payroll">Staff Member is not active</span>
      </div>
    </header>
    <div class="forms">
      <div v-for="(role, idx) in staffMember.value.roles" :key="role" class="form-data" @click="showFormsToggle(idx)">
        <div class="form-title">
          <font-awesome-icon icon="fa-user-tag" class="role-icon" />
          <h4>{{ role }}</h4>
          <font-awesome-icon icon="fa-chevron-down" class="chevron" :class="{ open: activeIdx === idx }" />
        </div>
        <Transition name="dropdown">
          <div v-if="activeIdx === idx" class="form-list">
            <p v-for="form in getStaffForms(role)" :key="form.id" @click.stop="openForm(form)">
              {{ form.title }}
              <font-awesome-icon :icon="isFormCompleted(form._id) ? 'fa-check' : 'fa-xmark'" :class="isFormCompleted(form._id) ? 'status-complete' : 'status-incomplete'" />
            </p>
            <p v-if="getStaffForms(role).length === 0" class="no-forms">No forms attached</p>
          </div>
        </Transition>
      </div>
    </div>
    <UserDetailsModal :isOpen="isOpen" :staff="staffMember" @close="closeUserDetails" @confirm="saveData" />
  </div>
</template>

<script>
import UserDetailsModal from "./UserDetailsModal.vue";
import { mapGetters } from "vuex";
export default {
  name: "StaffComponent",
  components: {
    UserDetailsModal,
  },
  computed: {
    ...mapGetters("training", ["getRoles", "getFormsViaRoles", "getForms"]),
    isInactive() {
      return this.staffMember.value.active === false;
    },
    formsProgress() {
      const roles = this.staffMember.value.roles || [];
      const allForms = roles.flatMap((role) => {
        const r = this.getFormsViaRoles.find((item) => item.role === role);
        return r ? r.forms : [];
      });
      const uniqueForms = [...new Map(allForms.map((f) => [f._id, f])).values()];
      const completed = uniqueForms.filter((f) => this.isFormCompleted(f._id)).length;
      return `${completed} / ${uniqueForms.length} forms completed`;
    },
  },
  data() {
    return {
      isOpen: false,
      forms: [],
      activeIdx: null,
    };
  },
  props: {
    staffMember: {
      type: Object,
      required: true,
    },
  },
  methods: {
    showFormsToggle(idx) {
      this.activeIdx = this.activeIdx === idx ? null : idx;
    },

    isFormCompleted(formId) {
      const training = this.staffMember.value.training || [];
      const entry = training.find((t) => t.formId === formId);
      if (!entry || !entry.updatedAt || entry.updatedAt.length === 0) return false;
      return entry.updatedAt[entry.updatedAt.length - 1].completed === true;
    },

    getStaffForms(role) {
      const r = this.getFormsViaRoles.find((item) => item.role === role);
      return r.forms;
    },

    openForm(form) {
      this.$store.dispatch("training/setActiveForm", { form: form, staffMember: this.staffMember });
      this.$router.push("/training/active/" + form._id + "/" + this.staffMember.value._id);
    },

    openUserDetails() {
      this.isOpen = true;
    },

    closeUserDetails() {
      this.isOpen = false;
    },

    saveData(updatedStaff) {
      this.$store.dispatch("training/updateEmployee", updatedStaff);
      this.isOpen = false;
    },
  },
  mounted() {
    // Add lifecycle hooks here
  },
};
</script>

<style scoped lang="scss">
.staff-component {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    flex-shrink: 0;

    &:hover .edit-icon {
      opacity: 1;
    }

    .avatar {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 50%;
      background-color: var(--opacity-background);
      color: var(--action-colour);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .staff-info {
      .name-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        h3 {
          margin: 0;
          font-size: 0.95rem;
          color: var(--text-colour);
          white-space: nowrap;
        }
        .manager-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          border-radius: 1rem;
          background-color: var(--action-colour);
          color: var(--primary-colour);
          white-space: nowrap;
        }

        .inactive-badge {
          display: inline-flex;
          align-items: center;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 0.15rem 0.5rem;
          border-radius: 1rem;
          background-color: var(--opacity-background);
          color: var(--text-colour);
          border: 1px solid var(--border-colour);
          white-space: nowrap;
        }
      }
      .payroll {
        font-size: 0.75rem;
        color: var(--action-colour);
        opacity: 0.8;
      }
    }
  }

  .forms {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: flex-end;

    .form-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      background-color: var(--secondary-colour);
      border-radius: 0.5rem;
      padding: 0.6rem 1rem;
      cursor: pointer;
      transition: background-color 0.2s ease;
      border: 1px solid transparent;

      &:has(.form-list) {
        background-color: var(--secondary-colour);
        border: 1px solid var(--action-colour);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        box-shadow: none;
        .form-title h4 {
          color: var(--text-colour);
        }
        .role-icon {
          color: var(--action-colour);
        }
        .chevron {
          color: var(--action-colour);
          opacity: 1;
        }
      }

      &:hover:not(:has(.form-list)) {
        border-color: var(--action-colour);
      }

      .form-title {
        display: flex;
        align-items: center;
        gap: 0.4rem;

        .role-icon {
          font-size: 0.75rem;
          color: var(--action-colour);
        }

        h4 {
          text-transform: capitalize;
          margin: 0;
          font-size: 0.8rem;
          white-space: nowrap;
          color: var(--text-colour);
        }

        .chevron {
          font-size: 0.65rem;
          color: var(--text-colour);
          opacity: 0.6;
          transition: transform 0.2s ease;
          &.open {
            transform: rotate(180deg);
          }
        }
      }
    }

    .form-list {
      display: flex;
      align-items: center;
      flex-direction: column;
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      min-width: 100%;
      gap: 0.4rem;
      padding: 0.5rem;
      z-index: 10;
      background-color: var(--primary-colour);
      border-radius: 0.5rem;
      box-shadow: var(--box-shadow);
    }

    .dropdown-enter-active,
    .dropdown-leave-active {
      transition: opacity 0.2s ease, transform 0.2s ease;
    }
    .dropdown-enter-from,
    .dropdown-leave-to {
      opacity: 0;
      transform: translateX(-50%) translateY(-6px);
    }
    .dropdown-enter-to,
    .dropdown-leave-from {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    p {
      display: flex;
      align-items: center;
      justify-content: space-evenly;
      padding: 0.4rem 0.6rem;
      margin: 0;
      border-radius: 0.3rem;
      background-color: var(--action-colour);
      color: var(--primary-colour);
      white-space: nowrap;
      text-transform: capitalize;
      cursor: pointer;
      font-size: 0.82rem;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: var(--secondary-colour);
        color: var(--action-colour);
      }

      .status-complete {
        color: var(--success-colour);
        background-color: var(--opacity-background);
        border-radius: 0.1rem;
        padding: 0.15rem;
        margin: 0 0.25rem;
      }
      
      .status-incomplete {
        color: var(--warning-colour);
        background-color: var(--opacity-background);
        border-radius: 0.1rem;
        padding: 0.15rem 0.25rem;
        margin: 0 0.25rem;
      }
    }
    
    .no-forms {
      background-color: transparent !important;
      color: var(--text-colour) !important;
      cursor: default !important;
      font-size: 0.78rem;
      opacity: 0.6;
      text-align: center;
    }

    p:not(.no-forms) ~ .no-forms {
      display: none;
    }
  }

  &.inactive {
    header {
      .avatar {
        background-color: rgba(212, 49, 49, 0.12);
        color: #d43131;
      }

      .staff-info {
        .payroll {
          color: #d43131;
        }
      }
    }
  }
}
</style>
